import React, { useId, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, SlidersHorizontal } from "lucide-react";
import { Input } from "../../../../shared/ui/controls/Input/Input";
import { FieldWrapper } from "../../../../shared/ui";
import Select from "../../../../shared/ui/controls/Select/Select";
import { WorkOrderStatusSelect } from "src/entities/work-order";
import { GewerkSearchSelect } from "src/entities/gewerk";
import {
  WORK_ORDER_SORT_OPTIONS,
  countActiveWorkOrderFilters,
  workOrderFilterField,
  type WorkOrderTextFilterKey,
} from "../model/filterFields";
import type { WorkOrderFilterLayoutProps } from "../model/filterLayout.types";

/**
 * How the text filters stack on a phone. Two per row where the value is short, full width where it
 * is a name or an address — the desktop's nine equal columns leave ~35 px each below 768 px, which
 * is narrower than the label above them.
 */
const PANEL_ROWS: Array<WorkOrderTextFilterKey[]> = [
  ["workOrderId", "datum"],
  ["mieter"],
  ["handwerker"],
  ["ve", "wohnung"],
  ["hauseingang"],
];

export const WorkOrderFilterBarMobile: React.FC<WorkOrderFilterLayoutProps> = ({
  filters,
  onFieldChange,
  onSortChange,
  onGewerkChange,
  onStatusChange,
  onReset,
}) => {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  const activeCount = countActiveWorkOrderFilters(filters);
  const isAscending = filters.sortDirection === "ASC";

  const toggleDirection = () => onSortChange(filters.sortField, isAscending ? "DESC" : "ASC");

  const renderField = (key: WorkOrderTextFilterKey) => {
    const field = workOrderFilterField(key);

    return (
      <FieldWrapper label={field.label} key={key}>
        <Input
          as="input"
          value={filters[key]}
          onChange={(value) => onFieldChange(key, value)}
          placeholder=""
          type={field.type ?? "text"}
          inputMode={field.inputMode}
        />
      </FieldWrapper>
    );
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex items-stretch gap-2">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls={panelId}
          className="flex h-11 min-w-0 flex-1 items-center justify-between gap-2 rounded-full border border-primary bg-white px-4 font-['PT_Sans'] text-[15px] text-primary"
        >
          <span className="flex min-w-0 items-center gap-2">
            <SlidersHorizontal size={16} className="shrink-0" />
            <span className="truncate">Filter</span>
            {activeCount > 0 && (
              <span className="flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[12px] font-bold leading-none text-white">
                {activeCount}
              </span>
            )}
          </span>
          <ChevronDown
            size={16}
            className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>

        <Select
          options={WORK_ORDER_SORT_OPTIONS}
          value={filters.sortField}
          onChange={(value) =>
            onSortChange(value as typeof filters.sortField, filters.sortDirection)
          }
          placeholder="Sortieren"
          className="min-w-0 flex-1"
          ariaLabel="Sortieren nach"
        />

        <button
          type="button"
          onClick={toggleDirection}
          // The label says what the button *is*, the state says what it will do — the icon alone
          // reads as decoration to a screen reader.
          aria-label={`Sortierrichtung: ${isAscending ? "aufsteigend" : "absteigend"}`}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-primary bg-white text-primary"
        >
          {isAscending ? (
            <ArrowUp size={18} aria-hidden="true" />
          ) : (
            <ArrowDown size={18} aria-hidden="true" />
          )}
        </button>
      </div>

      {open && (
        <div
          id={panelId}
          className="flex flex-col gap-3 rounded-2xl bg-white p-3 shadow-sm"
        >
          {PANEL_ROWS.map((row) => (
            <div
              key={row.join("-")}
              className={row.length > 1 ? "grid grid-cols-2 gap-3" : "grid grid-cols-1"}
            >
              {row.map(renderField)}
            </div>
          ))}

          <FieldWrapper label="Gewerk">
            <GewerkSearchSelect value={filters.gewerkId} onChange={onGewerkChange} />
          </FieldWrapper>

          <FieldWrapper label="Status">
            <WorkOrderStatusSelect
              value={filters.status}
              onChange={onStatusChange}
              withAllOption
            />
          </FieldWrapper>

          <button
            type="button"
            onClick={onReset}
            disabled={activeCount === 0}
            className="h-11 w-full rounded-full border border-primary bg-white font-['PT_Sans'] text-[15px] text-primary disabled:opacity-40"
          >
            Filter zurücksetzen
          </button>
        </div>
      )}
    </div>
  );
};
